import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { clamp } from 'lodash';

@Component({
  selector: 'mode-hue-selector',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './hue-selector.component.html',
  styleUrls: ['./hue-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HueComponent implements OnChanges, OnDestroy {
  @Input() hue = 0; // Hue is 0 <= Hue <= 360 (360 = 0)
  @Input() gradientHeight = 16;
  @Input() gradientWidth = 200;

  @Output() hueChanged = new EventEmitter<number>();
  @Output() moveStart = new EventEmitter();
  @Output() moveEnd = new EventEmitter();

  @ViewChild('container') container: ElementRef | undefined;
  @ViewChild('cursor') cursor: ElementRef | undefined;

  huePos = 0;

  private containerBoundingBox: DOMRect | undefined;
  private removeCursorMoveListenerFn: (() => void) | undefined;
  private removeCursorUpListenerFn: (() => void) | undefined;
  private removeKeyDownListenerFn: () => void;
  private removeKeyUpListenerFn: (() => void) | undefined;

  constructor(private renderer: Renderer2) {
    this.removeKeyDownListenerFn = this.renderer.listen('document', 'keydown', this.onKeyDown.bind(this));
  }

  ngOnChanges(): void {
    this.huePos = clamp((this.hue / 361) * this.gradientWidth, 0, this.gradientWidth);
  }

  ngOnDestroy(): void {
    this.removeListeners();
    this.removeKeyDownListenerFn();
  }

  emitSelectedHue() {
    const selectedHue = (this.huePos * 361) / this.gradientWidth;
    this.hueChanged.emit(selectedHue > 360 ? 0 : selectedHue);
  }

  setHue(event: MouseEvent) {
    this.huePos = event.offsetX;
    this.emitSelectedHue();

    this.onCursorMouseDown(event);
  }

  onCursorMouseDown(event: MouseEvent) {
    event.stopPropagation();
    this.removeListeners();
    this.moveStart.emit();
    this.containerBoundingBox = this.container?.nativeElement.getBoundingClientRect();
    if (this.container) {
      this.removeCursorMoveListenerFn = this.renderer.listen(
        'document',
        'mousemove',
        this.onCursorMouseMove.bind(this)
      );
      this.removeCursorUpListenerFn = this.renderer.listen('document', 'mouseup', this.onCursorMouseUp.bind(this));
    }
  }

  onCursorMouseMove(event: MouseEvent) {
    if (this.containerBoundingBox === undefined) {
      return;
    }
    // Position is calculated as the offset for the origin of the canvas.
    if (this.cursor) {
      this.huePos = clamp(event.clientX - this.containerBoundingBox.left, 0, this.containerBoundingBox.width);
      this.cursor.nativeElement.style.left = this.huePos + 'px';
      this.emitSelectedHue();
    }
  }

  onCursorMouseUp() {
    this.removeListeners();
    this.emitSelectedHue();
    this.moveEnd.emit();
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.target === this.container?.nativeElement) {
      switch (event.key) {
        case 'ArrowRight':
          this.huePos = Math.min((this.huePos += 1), this.gradientWidth);
          break;
        case 'ArrowLeft':
          this.huePos = Math.max((this.huePos -= 1), 0);
          break;
        case 'End':
          this.huePos = this.gradientWidth;
          break;
        case 'Home':
          this.huePos = 0;
          break;
        default:
          return;
      }
      if (this.removeKeyUpListenerFn == null) {
        this.removeKeyUpListenerFn = this.renderer.listen('document', 'keyup', this.onKeyUp.bind(this));
        this.moveStart.emit();
      }

      this.emitSelectedHue();
    }
  }

  onKeyUp() {
    this.moveEnd.emit();
    if (this.removeKeyUpListenerFn != null) {
      this.removeKeyUpListenerFn();
      this.removeKeyUpListenerFn = undefined;
    }
  }

  removeListeners() {
    if (this.removeCursorMoveListenerFn !== undefined) {
      this.removeCursorMoveListenerFn();
      this.removeCursorMoveListenerFn = undefined;
    }
    if (this.removeCursorUpListenerFn !== undefined) {
      this.removeCursorUpListenerFn();
      this.removeCursorUpListenerFn = undefined;
    }
  }
}
